import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import './CreatePatient.css';
import debounce from 'lodash.debounce';
import DentalChart from '../DentalChart/DentalChart';
import AccessControl from '../AccessControl';

const CreatePatient = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('generalInfo');
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const [patient, setPatient] = useState({
        dateOfExam: "",
        chartNumber: "",
        name: "",
        sex: "",
        birthdate: '',
        ageYear: 0,
        ageMonth: 0,
        guardianName: '',
        occupation: '',
        address: '',
        ContactInformation: '',
        email: '',
        chiefComplaint: "",
        medicalDentalHistory: '',
        orthodonticHistory: '',
        currentTreatment: false,
        congenitalAbnormalities: false,
        tmjProblems: false,
        oralHygiene: '',
        gingivalTissues: '',
        abnormalBleeding: false,
        asthma: false,
        cancer: false,
        congenitalHeartDefect: false,
        convulsionsEpilepsy: false,
        diabetes: false,
        handicapsDisabilities: false,
        hearingImpairment: false,
        heartMurmur: false,
        hemophilia: false,
        hepatitis: false,
        hivAids: false,
        kidneyLiverProblems: false,
        rheumaticScarletFever: false,
        tuberculosis: false,
        allergies: '',
        underCareOfPhysician: false,
        illnessComplication: false,
        isPregnant: false,
        menstrualPeriodRegular: false,
        additionalHealthInfo: '',
        clenchingGrindingTeeth: false,
        lipSuckingBiting: false,
        mouthBreather: false,
        musicalInstrument: false,
        nailBiting: false,
        speechProblems: false,
        nursingBottleHabits: false,
        thumbFingerSucking: false,
        tongueThrusting: false,
        MedicalAlert: '',
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle checkboxes (if they are part of the input)
        if (type === 'checkbox') {
            setPatient((prevState) => ({
                ...prevState,
                [name]: checked, // Set to true or false based on checked state
            }));
        } else {
            setPatient((prevState) => ({
                ...prevState,
                [name]: value, // Update only the field that changed
            }));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!patient.name || !patient.sex || !patient.birthdate || !patient.address || !patient.ContactInformation || !patient.email) {
            alert('Please fill out all required fields.');
            return;
        }
        const response = await fetch('https://api.hgcdental.clinic/api/patients', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(patient)
        });
        const json = await response.json();

        if (response.ok) {
            navigate('/patients');
        } else {
            console.error('Error:', json.error);
        }
    };

    const GeneralInfo = () => (
        <div>
            {/* <p>Chief Complaint: {patient.chiefComplaint}</p> */}
            <div className="input-group">
                <label className="label">Chief Complaint</label>
                <input
                    type="text"
                    name="chiefComplaint"
                    value={patient.chiefComplaint || ""}
                    onChange={handleChange}
                    className="input-field"
                />
            </div>
            <div className="input-group">
                <label className="label">Past Medical/Dental History</label>
                <input
                    type="text"
                    name="medicalDentalHistory"
                    value={patient.medicalDentalHistory}
                    onChange={handleChange}
                    className="input-field"
                />
            </div>
            <div className="input-group">
                <label className="label">Prior Orthodontic History</label>
                <input
                    type="text"
                    name="orthodonticHistory"
                    value={patient.orthodonticHistory}
                    onChange={handleChange}
                    className="input-field"
                />
            </div>
            <div className="checkbox-container">
                <label className="label">Under any treatment or medication now?</label>
                <input
                    type="checkbox"
                    name="currentTreatment"
                    checked={patient.currentTreatment}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>
            <div className="checkbox-container">
                <label className="label">Congenital Abnormalities</label>
                <input
                    type="checkbox"
                    name="congenitalAbnormalities"
                    checked={patient.congenitalAbnormalities}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>
            <div className="checkbox-container">
                <label className="label">Temporo Mandibular Joint Problems</label>
                <input
                    type="checkbox"
                    name="tmjProblems"
                    checked={patient.tmjProblems}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>
            <div className="input-group">
                <label className="label">Oral Hygiene</label>
                <select name="oralHygiene" value={patient.oralHygiene} onChange={handleChange} className="input-field">
                    <option value="">Select</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                </select>
            </div>
            <div className="input-group">
                <label className="label">Gingival Tissues</label>
                <select name="gingivalTissues" value={patient.gingivalTissues} onChange={handleChange} className="input-field">
                    <option value="">Select</option>
                    <option value="Thick">Thick</option>
                    <option value="Thin">Thin</option>
                    <option value="Normal">Normal</option>
                    <option value="Receding">Receding</option>
                </select>
            </div>
        </div>
    );

    const MedicalInformation = () => (
        <div>
            <h3>Please encircle the word YES/NO as the case
                may be in answering the question as follows:
            </h3>
            <h3>1. Have you ever been treated for any</h3>

            <div className="checkbox-container">
                <label className="label">Abnormal Bleeding</label>
                <input
                    type="checkbox"
                    name="abnormalBleeding"
                    checked={patient.abnormalBleeding}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Asthma</label>
                <input
                    type="checkbox"
                    name="asthma"
                    checked={patient.asthma}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Cancer</label>
                <input
                    type="checkbox"
                    name="cancer"
                    checked={patient.cancer}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Congenital Heart Defect</label>
                <input
                    type="checkbox"
                    name="congenitalHeartDefect"
                    checked={patient.congenitalHeartDefect}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Convulsions/Epilepsy</label>
                <input
                    type="checkbox"
                    name="convulsionsEpilepsy"
                    checked={patient.convulsionsEpilepsy}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Diabetes</label>
                <input
                    type="checkbox"
                    name="diabetes"
                    checked={patient.diabetes}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Handicaps/Disabilities</label>
                <input
                    type="checkbox"
                    name="handicapsDisabilities"
                    checked={patient.handicapsDisabilities}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Hearing Impairment</label>
                <input
                    type="checkbox"
                    name="hearingImpairment"
                    checked={patient.hearingImpairment}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Heart Murmur</label>
                <input
                    type="checkbox"
                    name="heartMurmur"
                    checked={patient.heartMurmur}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Hemophilia</label>
                <input
                    type="checkbox"
                    name="hemophilia"
                    checked={patient.hemophilia}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Hepatitis</label>
                <input
                    type="checkbox"
                    name="hepatitis"
                    checked={patient.hepatitis}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">HIV +/AIDS</label>
                <input
                    type="checkbox"
                    name="hivAids"
                    checked={patient.hivAids}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Kidney/Liver Problems</label>
                <input
                    type="checkbox"
                    name="kidneyLiverProblems"
                    checked={patient.kidneyLiverProblems}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Rheumatic/Scarlet Fever</label>
                <input
                    type="checkbox"
                    name="rheumaticScarletFever"
                    checked={patient.rheumaticScarletFever}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Tuberculosis (TB)</label>
                <input
                    type="checkbox"
                    name="tuberculosis"
                    checked={patient.tuberculosis}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <h3>2. Are you allergic to any drugs (penicillin),
                latex, metals, plastics, anesthetics, foods,
                materials or pollen?</h3>
            <div className="input-group">
                <label className="label">Allergies</label>
                <input
                    type="text"
                    name="allergies"
                    value={patient.allergies}
                    onChange={handleChange}
                    className="input-field"
                />
            </div>

            <h3>3. Are you presently or have you, during the
                past two years, been under the care of a
                physician?</h3>
            <div className="checkbox-container">
                <label className="label">Under Care of Physician</label>
                <input
                    type="checkbox"
                    name="underCareOfPhysician"
                    checked={patient.underCareOfPhysician}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <h3>4. Have you ever had an illness/complication
                following dental treatment of any kind?
            </h3>
            <div className="checkbox-container">
                <label className="label">Illness/Complication</label>
                <input
                    type="checkbox"
                    name="illnessComplication"
                    checked={patient.illnessComplication}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <h3>5. If you are a woman,</h3>
            <div className="checkbox-container">
                <label className="label">Are you pregnant?</label>
                <input
                    type="checkbox"
                    name="isPregnant"
                    checked={patient.isPregnant}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Is your menstrual period regular</label>
                <input
                    type="checkbox"
                    name="menstrualPeriodRegular"
                    checked={patient.menstrualPeriodRegular}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <h3>6. Is there any additional information about
                your general health which should be
                disclosed in confidence?</h3>
            <div className="input-group">
                <label className="label">Additional Health Information</label>
                <input
                    type="text"
                    name="additionalHealthInfo"
                    value={patient.additionalHealthInfo}
                    onChange={handleChange}
                    className="input-field"
                />
            </div>

            <h3>Oral Habits</h3>
            <div className="checkbox-container">
                <label className="label">Clenching/Grinding Teeth</label>
                <input
                    type="checkbox"
                    name="clenchingGrindingTeeth"
                    checked={patient.clenchingGrindingTeeth}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Lip Sucking/Biting</label>
                <input
                    type="checkbox"
                    name="lipSuckingBiting"
                    checked={patient.lipSuckingBiting}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Mouth Breather</label>
                <input
                    type="checkbox"
                    name="mouthBreather"
                    checked={patient.mouthBreather}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Musical Instrument</label>
                <input
                    type="checkbox"
                    name="musicalInstrument"
                    checked={patient.musicalInstrument}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Nail Biting</label>
                <input
                    type="checkbox"
                    name="nailBiting"
                    checked={patient.nailBiting}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Speech Problems</label>
                <input
                    type="checkbox"
                    name="speechProblems"
                    checked={patient.speechProblems}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Nursing Bottle Habits</label>
                <input
                    type="checkbox"
                    name="nursingBottleHabits"
                    checked={patient.nursingBottleHabits}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>
            <div className="checkbox-container">
                <label className="label">Thumb/Finger Sucking</label>
                <input
                    type="checkbox"
                    name="thumbFingerSucking"
                    checked={patient.thumbFingerSucking}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>

            <div className="checkbox-container">
                <label className="label">Tongue Thrusting</label>
                <input
                    type="checkbox"
                    name="tongueThrusting"
                    checked={patient.tongueThrusting}
                    onChange={handleChange}
                    className="checkbox-field"
                />
            </div>
        </div>
    );

    const DentalChartTab = () => (
        <div>
            <DentalChart />
        </div>
    );

    const renderActiveTabContent = () => {
        switch (activeTab) {
            case 'GeneralInfo':
                return <GeneralInfo />
            case 'MedicalInformation':
                return <MedicalInformation />;
            case 'dentalChart':
                return <DentalChartTab />;
            default:
                return <GeneralInfo />
        }
    };

    return (
        <AccessControl>
            <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Create Patient" toggleSidebar={toggleSidebar} />
                <div className="create-patient-content">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="create-patient">
                        <div>
                            <button className="create-patient-button" onClick={handleSubmit} >Save</button>
                            <button type="button" onClick={() => setPatient({
                                dateOfExam: "",
                                chartNumber: "",
                                name: "",
                                sex: "",
                                birthdate: '',
                                ageYear: 0,
                                ageMonth: 0,
                                guardianName: '',
                                occupation: '',
                                address: '',
                                ContactInformation: '',
                                email: '',
                                chiefComplaint: "",
                                medicalDentalHistory: '',
                                orthodonticHistory: '',
                                currentTreatment: false,
                                congenitalAbnormalities: false,
                                tmjProblems: false,
                                oralHygiene: '',
                                gingivalTissues: '',
                                abnormalBleeding: false,
                                asthma: false,
                                cancer: false,
                                congenitalHeartDefect: false,
                                convulsionsEpilepsy: false,
                                diabetes: false,
                                handicapsDisabilities: false,
                                hearingImpairment: false,
                                heartMurmur: false,
                                hemophilia: false,
                                hepatitis: false,
                                hivAids: false,
                                kidneyLiverProblems: false,
                                rheumaticScarletFever: false,
                                tuberculosis: false,
                                allergies: '',
                                underCareOfPhysician: false,
                                illnessComplication: false,
                                isPregnant: false,
                                menstrualPeriodRegular: false,
                                additionalHealthInfo: '',
                                clenchingGrindingTeeth: false,
                                lipSuckingBiting: false,
                                mouthBreather: false,
                                musicalInstrument: false,
                                nailBiting: false,
                                speechProblems: false,
                                nursingBottleHabits: false,
                                thumbFingerSucking: false,
                                tongueThrusting: false,
                                MedicalAlert: '',
                            })} className="create-patient-button-1">
                                Reset
                            </button>
                        </div>
                        <div className="create-patient-form">
                            <h2>Create New Patient</h2>
                            <div className="input-group">
                                <label className="label">Name</label>
                                <input type="text" name="name" value={patient.name} onChange={handleChange} className="input-field" />
                            </div>
                            <div className="input-group">
                                <label className="label">Sex</label>
                                <select name="sex" value={patient.sex} onChange={handleChange} className="input-field">
                                    <option value="">Select</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label className="label">Date of Birth</label>
                                <input type="date" name="birthdate" value={patient.birthdate} onChange={handleChange} className="input-field" />
                            </div>
                            <div className="input-group">
                                <label className="label">Age</label>
                                <div className="age-inputs">
                                    <input type="number" name="ageYear" value={patient.ageYear} onChange={handleChange} placeholder="Years" className="age-input" />
                                    <span>years and</span>
                                    <input type="number" name="ageMonth" value={patient.ageMonth} onChange={handleChange} placeholder="Months" className="age-input" />
                                    <span>months</span>
                                </div>
                            </div>
                            <div className="input-group">
                                <label className="label">Parent's/Guardian's Name</label>
                                <input type="text" name="guardianName" value={patient.guardianName} onChange={handleChange} className="input-field" />
                            </div>
                            <div className="input-group">
                                <label className="label">Occupation</label>
                                <input type="text" name="occupation" value={patient.occupation} onChange={handleChange} className="input-field" />
                            </div>
                            <div className="input-group">
                                <label className="label">Address</label>
                                <input type="text" name="address" value={patient.address} onChange={handleChange} className="input-field" />
                            </div>
                            <div className="input-group">
                                <label className="label">Contact Number</label>
                                <input type="tel" name="ContactInformation" value={patient.ContactInformation} onChange={handleChange} className="input-field" />
                            </div>
                            <div className="input-group">
                                <label className="label">Email</label>
                                <input type="text" name="email" value={patient.email} onChange={handleChange} className="input-field" />
                            </div>
                            {/* Tab Navigation */}
                            <br />
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '-1px', cursor: 'pointer' }}>
                                <span
                                    onClick={() => setActiveTab('GeneralInfo')}
                                    className={`tab ${activeTab === 'GeneralInfo' ? 'active' : ''}`}
                                >
                                    General Information
                                </span>
                                <span
                                    onClick={() => setActiveTab('MedicalInformation')}
                                    className={`tab ${activeTab === 'MedicalInformation' ? 'active' : ''}`}
                                >
                                    Medical Information
                                </span>
                                <span
                                    onClick={() => setActiveTab('dentalChart')}
                                    className={`tab ${activeTab === 'dentalChart' ? 'active' : ''}`}
                                >
                                    Dental Chart
                                </span>
                            </div>
                            <div>
                                {/* Render the active tab content within the same page */}
                                <div className="create-patient-tab-content">
                                    {renderActiveTabContent()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default CreatePatient;